import { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Plus, CheckCircle2, XCircle, Copy, Globe, Mail, Loader2, Pencil, Activity, RefreshCw, ShieldAlert, ShieldCheck, ShieldQuestion, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

const API = `${window.location.origin}/api`;

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);


/** Compact status pill shown in each salon row. */
function HealthBadge({ tenant, onClick }) {
  const dns = tenant.dns_status;
  const tls = tenant.tls_status;

  let label, Icon, classes;
  if (dns === 'ok' && tls === 'ok') {
    label = 'Live'; Icon = ShieldCheck;
    classes = 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100';
  } else if (dns === 'ok' && tls === 'provisioning') {
    label = 'TLS issuing'; Icon = Clock;
    classes = 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100';
  } else if (dns === 'fail') {
    label = 'DNS missing'; Icon = ShieldAlert;
    classes = 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100';
  } else if (dns === 'ok' && tls === 'fail') {
    label = 'Unreachable'; Icon = ShieldAlert;
    classes = 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100';
  } else {
    label = 'Not checked'; Icon = ShieldQuestion;
    classes = 'bg-muted text-muted-foreground border-border hover:bg-muted/80';
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`health-badge-${tenant.id}`}
      title={tenant.last_check_detail || 'Click for details'}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors cursor-pointer ${classes}`}
    >
      <Icon size={11} />
      <span>{label}</span>
    </button>
  );
}

export default function SuperAdminSalons() {
  const { user, loading: authLoading } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [newTenantInfo, setNewTenantInfo] = useState(null);
  const [editTarget, setEditTarget] = useState(null);     // tenant being edited
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);   // tenant whose admin password we're resetting
  const [resetForm, setResetForm] = useState({ admin_email: '', admin_password: '' });
  const [savingReset, setSavingReset] = useState(false);
  const [probingIds, setProbingIds] = useState({});       // map<tenantId, bool> of which rows are currently re-checking
  const [healthDetailFor, setHealthDetailFor] = useState(null); // tenant whose health-detail modal is open

  // Form state
  const [form, setForm] = useState({
    display_name: '',
    slug: '',
    subdomain: '',
    custom_domain: '',
    admin_email: '',
    admin_password: '',
  });

  const fetchTenants = async () => {
    try {
      const res = await axios.get(`${API}/super-admin/tenants`);
      setTenants(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load salons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.isPrimary) fetchTenants();
    else if (user) setLoading(false);
  }, [user]);

  // Auto-probe any tenant that has never been health-checked. Runs once per page mount.
  useEffect(() => {
    if (loading) return;
    const unchecked = tenants.filter((t) => !t.last_checked_at);
    if (unchecked.length === 0) return;
    unchecked.forEach((t) => {
      // Don't await — let them complete in the background
      recheckStatus(t).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleNameChange = (val) => {
    setForm((f) => {
      // Auto-fill slug + subdomain from display name unless user has typed something custom
      const autoSlug = slugify(val);
      const slugLooksAuto = !f.slug || f.slug === slugify(f.display_name);
      return {
        ...f,
        display_name: val,
        slug: slugLooksAuto ? autoSlug : f.slug,
        subdomain: slugLooksAuto ? autoSlug : f.subdomain,
      };
    });
  };

  const handleCreate = async () => {
    if (!form.display_name || !form.slug) {
      toast.error('Salon name and slug are required');
      return;
    }
    if (form.admin_email && form.admin_password.length < 6) {
      toast.error('Admin password must be at least 6 characters');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/super-admin/tenants`, {
        slug: form.slug,
        display_name: form.display_name,
        subdomain: form.subdomain || form.slug,
        custom_domain: form.custom_domain || null,
        admin_email: form.admin_email || undefined,
        admin_password: form.admin_password || undefined,
      });
      toast.success(`${form.display_name} created`);
      setNewTenantInfo({
        ...res.data,
        admin_email: form.admin_email,
        admin_password: form.admin_password,
      });
      setCreateOpen(false);
      setForm({ display_name: '', slug: '', subdomain: '', custom_domain: '', admin_email: '', admin_password: '' });
      if (form.admin_email && form.admin_password) {
        setCredentialsOpen(true);
      }
      fetchTenants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create salon');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (tenant) => {
    if (tenant.is_primary && tenant.is_active) {
      toast.error("Can't deactivate the primary tenant");
      return;
    }
    try {
      await axios.put(`${API}/super-admin/tenants/${tenant.id}`, { is_active: !tenant.is_active });
      toast.success(`${tenant.display_name} ${!tenant.is_active ? 'activated' : 'deactivated'}`);
      fetchTenants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update salon');
    }
  };

  const openEdit = (tenant) => {
    setEditTarget(tenant);
    setEditForm({
      display_name: tenant.display_name || '',
      subdomain: tenant.subdomain || '',
      custom_domain: tenant.custom_domain || '',
    });
  };

  const saveEdit = async () => {
    if (!editTarget || !editForm) return;
    if (!editForm.display_name.trim()) {
      toast.error('Display name is required');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/super-admin/tenants/${editTarget.id}`, {
        display_name: editForm.display_name.trim(),
        subdomain: editForm.subdomain ? slugify(editForm.subdomain) : null,
        custom_domain: editForm.custom_domain ? editForm.custom_domain.trim().toLowerCase() : null,
      });
      toast.success(`${editForm.display_name} updated`);
      setEditTarget(null);
      setEditForm(null);
      fetchTenants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update salon');
    } finally {
      setSavingEdit(false);
    }
  };

  const openReset = (tenant) => {
    setResetTarget(tenant);
    setResetForm({ admin_email: '', admin_password: '' });
  };

  const saveReset = async () => {
    if (!resetTarget) return;
    if (!resetForm.admin_email || resetForm.admin_password.length < 6) {
      toast.error('Email and a 6+ char password are required');
      return;
    }
    setSavingReset(true);
    try {
      await axios.post(`${API}/super-admin/tenants/${resetTarget.id}/seed-admin`, resetForm);
      toast.success(`Admin login updated for ${resetTarget.display_name}`);
      setNewTenantInfo({
        ...resetTarget,
        admin_email: resetForm.admin_email,
        admin_password: resetForm.admin_password,
      });
      setResetTarget(null);
      setCredentialsOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update admin login');
    } finally {
      setSavingReset(false);
    }
  };

  const copyToClipboard = (text, label = 'Copied') => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const recheckStatus = async (tenant) => {
    setProbingIds((p) => ({ ...p, [tenant.id]: true }));
    try {
      const res = await axios.post(`${API}/super-admin/tenants/${tenant.id}/recheck-status`);
      const newDoc = res.data;
      const tlsOk = newDoc.tls_status === 'ok';
      const tlsProvisioning = newDoc.tls_status === 'provisioning';
      if (tlsOk) toast.success(`${tenant.display_name} is fully live`);
      else if (tlsProvisioning) toast.message('TLS cert still issuing — usually completes within 10–30 min');
      else toast.warning(`${tenant.display_name}: ${newDoc.last_check_detail || 'check failed'}`);
      // Merge the updated tenant into local list
      setTenants((list) => list.map((t) => (t.id === tenant.id ? { ...t, ...newDoc } : t)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-check failed');
    } finally {
      setProbingIds((p) => ({ ...p, [tenant.id]: false }));
    }
  };

  // Stats summary
  const totalSalons = tenants.length;
  const activeSalons = tenants.filter((t) => t.is_active).length;
  const inactiveSalons = totalSalons - activeSalons;
  const liveSalons = tenants.filter((t) => t.is_active && t.dns_status === 'ok' && t.tls_status === 'ok').length;

  // ── Guards ──
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="super-admin-loading">
        <Loader2 className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user?.isPrimary) {
    return (
      <Card className="max-w-xl mx-auto mt-12" data-testid="super-admin-forbidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><XCircle className="text-destructive" size={20} /> Access denied</CardTitle>
          <CardDescription>This screen is only available to the platform owner from the primary tenant host.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-admin-salons-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground/70 mb-1">
            <Building2 size={12} /> Super-admin
          </div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>
            Salons on B∞kd
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage every salon hosted on this deployment. Each one has its own path on bookdbeyond.com, settings, data and login.
          </p>
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="open-create-salon-btn" className="self-start sm:self-auto">
              <Plus size={16} className="mr-1.5" /> Add salon
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="create-salon-dialog">
            <DialogHeader>
              <DialogTitle>Onboard a new salon</DialogTitle>
              <DialogDescription>
                Creates an isolated tenant + an admin login. They'll be reachable at{' '}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  bookdbeyond.com/{form.slug || 'slug'}
                </code>
                .
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2">
              <div>
                <Label htmlFor="salon-name">Salon name</Label>
                <Input
                  id="salon-name"
                  data-testid="salon-name-input"
                  value={form.display_name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Lauren Aesthetics"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="salon-slug">Slug</Label>
                  <Input
                    id="salon-slug"
                    data-testid="salon-slug-input"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
                    placeholder="lauren-aesthetics"
                  />
                </div>
                <div>
                  <Label htmlFor="salon-subdomain">Subdomain</Label>
                  <Input
                    id="salon-subdomain"
                    data-testid="salon-subdomain-input"
                    value={form.subdomain}
                    onChange={(e) => setForm((f) => ({ ...f, subdomain: slugify(e.target.value) }))}
                    placeholder="lauren-aesthetics"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="salon-custom-domain">Custom domain (optional)</Label>
                <Input
                  id="salon-custom-domain"
                  data-testid="salon-custom-domain-input"
                  value={form.custom_domain}
                  onChange={(e) => setForm((f) => ({ ...f, custom_domain: e.target.value.trim().toLowerCase() }))}
                  placeholder="laurenaesthetics.com"
                />
              </div>

              <div className="pt-2 mt-2 border-t border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground/70 mb-2">Initial admin login</p>
                <div className="space-y-2">
                  <div>
                    <Label htmlFor="admin-email">Email</Label>
                    <Input
                      id="admin-email"
                      data-testid="admin-email-input"
                      type="email"
                      value={form.admin_email}
                      onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value.trim() }))}
                      placeholder="lauren@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin-password">Temporary password</Label>
                    <Input
                      id="admin-password"
                      data-testid="admin-password-input"
                      type="text"
                      value={form.admin_password}
                      onChange={(e) => setForm((f) => ({ ...f, admin_password: e.target.value }))}
                      placeholder="Min 6 characters"
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setCreateOpen(false)} data-testid="cancel-create-btn">
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={creating} data-testid="confirm-create-salon-btn">
                {creating ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
                Create salon
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats summary */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="salons-stats">
          <Card className="border-dashed">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground/70 mb-1">
                <Building2 size={12} /> Total
              </div>
              <p className="text-2xl font-semibold tabular-nums" data-testid="stat-total">{totalSalons}</p>
              <p className="text-[10px] text-muted-foreground">salons on B∞kd</p>
            </CardContent>
          </Card>
          <Card className="border-dashed border-emerald-200 bg-emerald-50/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-700/80 mb-1">
                <Activity size={12} /> Active
              </div>
              <p className="text-2xl font-semibold tabular-nums text-emerald-700" data-testid="stat-active">{activeSalons}</p>
              <p className="text-[10px] text-emerald-700/70">switched on</p>
            </CardContent>
          </Card>
          <Card className="border-dashed border-emerald-300 bg-emerald-100/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-800/80 mb-1">
                <ShieldCheck size={12} /> Fully live
              </div>
              <p className="text-2xl font-semibold tabular-nums text-emerald-800" data-testid="stat-live">{liveSalons}</p>
              <p className="text-[10px] text-emerald-800/70">DNS + TLS verified</p>
            </CardContent>
          </Card>
          <Card className="border-dashed">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground/70 mb-1">
                <XCircle size={12} /> Inactive
              </div>
              <p className="text-2xl font-semibold tabular-nums text-muted-foreground" data-testid="stat-inactive">{inactiveSalons}</p>
              <p className="text-[10px] text-muted-foreground">paused or in setup</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tenant list */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="grid gap-3" data-testid="salons-list">
          {tenants.map((t) => (
            <Card key={t.id} className="hover:shadow-sm transition-shadow" data-testid={`salon-row-${t.id}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-base">{t.display_name}</h3>
                    {t.is_primary && (
                      <Badge variant="secondary" className="text-[10px]">PRIMARY</Badge>
                    )}
                    {!t.is_active && (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">Inactive</Badge>
                    )}
                    <HealthBadge tenant={t} onClick={() => setHealthDetailFor(t)} />
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="font-mono">{t.slug}</span>
                    <span className="flex items-center gap-1">
                      <Globe size={11} />
                      <a href={`https://bookdbeyond.com/${t.slug}`} target="_blank" rel="noreferrer" className="hover:underline">
                        bookdbeyond.com/{t.slug}
                      </a>
                    </span>
                    {t.last_checked_at && (
                      <span className="text-[10px] text-muted-foreground/70">
                        checked {new Date(t.last_checked_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => recheckStatus(t)}
                    disabled={!!probingIds[t.id]}
                    data-testid={`recheck-status-${t.id}`}
                    title="Re-check DNS + TLS"
                  >
                    {probingIds[t.id] ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(`https://bookdbeyond.com/${t.slug}`, 'Salon URL')}
                    data-testid={`copy-domain-${t.id}`}
                    title="Copy salon URL"
                  >
                    <Copy size={13} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(t)}
                    data-testid={`edit-tenant-${t.id}`}
                    title="Edit salon details"
                  >
                    <Pencil size={13} className="mr-1.5" /> Edit
                  </Button>
                  {!t.is_primary && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openReset(t)}
                      data-testid={`reset-admin-${t.id}`}
                      title="Reset admin login"
                    >
                      <Mail size={13} className="mr-1.5" /> Reset login
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant={t.is_active ? 'outline' : 'default'}
                    onClick={() => toggleActive(t)}
                    disabled={t.is_primary && t.is_active}
                    data-testid={`toggle-active-${t.id}`}
                  >
                    {t.is_active ? (
                      <><CheckCircle2 size={13} className="mr-1.5 text-emerald-600" /> Active</>
                    ) : (
                      <>Reactivate</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Health detail dialog */}
      <Dialog open={!!healthDetailFor} onOpenChange={(o) => { if (!o) setHealthDetailFor(null); }}>
        <DialogContent className="max-w-lg" data-testid="health-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {healthDetailFor?.tls_status === 'ok' && healthDetailFor?.dns_status === 'ok' ? (
                <ShieldCheck size={20} className="text-emerald-600" />
              ) : healthDetailFor?.tls_status === 'provisioning' ? (
                <Clock size={20} className="text-amber-600" />
              ) : (
                <ShieldAlert size={20} className="text-red-600" />
              )}
              Health · {healthDetailFor?.display_name}
            </DialogTitle>
            <DialogDescription>
              Probe results for{' '}
              <code className="bg-muted px-1 py-0.5 rounded text-[10px]">
                {healthDetailFor ? `bookdbeyond.com/${healthDetailFor.slug}` : 'no slug'}
              </code>
            </DialogDescription>
          </DialogHeader>
          {healthDetailFor && (
            <div className="space-y-3 py-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">DNS</div>
                  <div className="flex items-center gap-2">
                    {healthDetailFor.dns_status === 'ok' ? (
                      <><CheckCircle2 size={16} className="text-emerald-600" /> <span className="font-medium">Resolves</span></>
                    ) : healthDetailFor.dns_status === 'fail' ? (
                      <><XCircle size={16} className="text-red-600" /> <span className="font-medium">Not resolving</span></>
                    ) : (
                      <><ShieldQuestion size={16} className="text-muted-foreground" /> <span className="font-medium">Not checked</span></>
                    )}
                  </div>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">TLS / HTTPS</div>
                  <div className="flex items-center gap-2">
                    {healthDetailFor.tls_status === 'ok' ? (
                      <><ShieldCheck size={16} className="text-emerald-600" /> <span className="font-medium">Valid cert</span></>
                    ) : healthDetailFor.tls_status === 'provisioning' ? (
                      <><Clock size={16} className="text-amber-600" /> <span className="font-medium">Issuing…</span></>
                    ) : healthDetailFor.tls_status === 'fail' ? (
                      <><ShieldAlert size={16} className="text-red-600" /> <span className="font-medium">Unreachable</span></>
                    ) : (
                      <><ShieldQuestion size={16} className="text-muted-foreground" /> <span className="font-medium">Not checked</span></>
                    )}
                  </div>
                </div>
              </div>

              {healthDetailFor.last_check_detail && (
                <div className="rounded-lg border border-border bg-muted/40 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Probe detail</div>
                  <p className="text-xs font-mono break-words text-muted-foreground">{healthDetailFor.last_check_detail}</p>
                </div>
              )}

              {/* Helpful next steps based on state */}
              {healthDetailFor.dns_status === 'fail' && (
                <div className="rounded-lg border border-red-200 bg-red-50/60 p-3">
                  <p className="text-xs font-semibold text-red-800 mb-1">⚠️ Can't reach bookdbeyond.com</p>
                  <p className="text-xs text-red-900">
                    The probe couldn't resolve <code className="bg-white/60 px-1 py-0.5 rounded">bookdbeyond.com</code>. This usually means the deployment's custom domain isn't pointed at this build. Check Emergent → Deployments → Custom domains.
                  </p>
                </div>
              )}
              {healthDetailFor.dns_status === 'ok' && healthDetailFor.tls_status === 'provisioning' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                  <p className="text-xs font-semibold text-amber-800 mb-1">⏳ TLS certificate is still being issued</p>
                  <ol className="text-xs text-amber-900 list-decimal pl-4 space-y-1">
                    <li>DNS resolves correctly ✓</li>
                    <li>Make sure the hostname is added in your Emergent production deployment's custom-domains panel.</li>
                    <li>Cert issuance usually completes within 10–30 minutes of adding the domain.</li>
                    <li>If still failing after an hour, email <code className="bg-white/60 px-1 py-0.5 rounded">support@emergent.sh</code> with your job ID.</li>
                  </ol>
                </div>
              )}
              {healthDetailFor.dns_status === 'ok' && healthDetailFor.tls_status === 'ok' && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                  <p className="text-xs font-semibold text-emerald-800">✅ This salon is fully live — customers can reach it over HTTPS.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { if (healthDetailFor) recheckStatus(healthDetailFor); }}
              disabled={!!(healthDetailFor && probingIds[healthDetailFor.id])}
              data-testid="modal-recheck-btn"
            >
              {healthDetailFor && probingIds[healthDetailFor.id]
                ? <Loader2 size={14} className="animate-spin mr-1.5" />
                : <RefreshCw size={14} className="mr-1.5" />}
              Re-check now
            </Button>
            <Button onClick={() => setHealthDetailFor(null)} data-testid="close-health-detail-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit tenant dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) { setEditTarget(null); setEditForm(null); } }}>
        <DialogContent className="max-w-md" data-testid="edit-salon-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editTarget?.display_name}</DialogTitle>
            <DialogDescription>
              The slug <code className="bg-muted px-1 py-0.5 rounded text-[10px]">{editTarget?.slug}</code>{' '}
              is permanent (used as the tenant id). Display name, subdomain and custom domain can be changed.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3 py-2">
              <div>
                <Label htmlFor="edit-display-name">Display name</Label>
                <Input
                  id="edit-display-name"
                  data-testid="edit-display-name-input"
                  value={editForm.display_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, display_name: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="edit-subdomain">Subdomain</Label>
                <Input
                  id="edit-subdomain"
                  data-testid="edit-subdomain-input"
                  value={editForm.subdomain}
                  onChange={(e) => setEditForm((f) => ({ ...f, subdomain: slugify(e.target.value) }))}
                  placeholder="e.g. lauren-aesthetics"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Resolves to {editForm.subdomain || 'subdomain'}.bookdbeyond.com</p>
              </div>
              <div>
                <Label htmlFor="edit-custom-domain">Custom domain</Label>
                <Input
                  id="edit-custom-domain"
                  data-testid="edit-custom-domain-input"
                  value={editForm.custom_domain}
                  onChange={(e) => setEditForm((f) => ({ ...f, custom_domain: e.target.value.trim().toLowerCase() }))}
                  placeholder="e.g. laurenaesthetics.com"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Optional — leave blank to use the subdomain.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditTarget(null); setEditForm(null); }} data-testid="cancel-edit-btn">Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="save-edit-btn">
              {savingEdit ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Pencil size={14} className="mr-1.5" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset admin login dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) setResetTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="reset-admin-dialog">
          <DialogHeader>
            <DialogTitle>Reset admin login</DialogTitle>
            <DialogDescription>
              Set or replace the admin login for <strong>{resetTarget?.display_name}</strong>. Any existing admin with the same email will have their password overwritten. The previous password is gone — you can't recover it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="reset-email">Admin email</Label>
              <Input
                id="reset-email"
                data-testid="reset-admin-email-input"
                type="email"
                value={resetForm.admin_email}
                onChange={(e) => setResetForm((f) => ({ ...f, admin_email: e.target.value.trim() }))}
                placeholder="admin@example.com"
              />
            </div>
            <div>
              <Label htmlFor="reset-password">New temporary password</Label>
              <Input
                id="reset-password"
                data-testid="reset-admin-password-input"
                type="text"
                value={resetForm.admin_password}
                onChange={(e) => setResetForm((f) => ({ ...f, admin_password: e.target.value }))}
                placeholder="Min 6 characters"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetTarget(null)} data-testid="cancel-reset-btn">Cancel</Button>
            <Button onClick={saveReset} disabled={savingReset} data-testid="save-reset-btn">
              {savingReset ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Mail size={14} className="mr-1.5" />}
              Reset login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials reveal dialog */}
      <Dialog open={credentialsOpen} onOpenChange={setCredentialsOpen}>
        <DialogContent className="max-w-md" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={18} /> Share these credentials
            </DialogTitle>
            <DialogDescription>
              Send this login to <strong>{newTenantInfo?.display_name}</strong>. This is the only time you'll see the password unsmasked — it's hashed in the database.
            </DialogDescription>
          </DialogHeader>
          {newTenantInfo && (
            <div className="space-y-3 py-2">
              <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground text-xs">Login URL</span>
                  <code className="text-xs flex-1 text-right truncate">
                    https://{newTenantInfo.subdomain || newTenantInfo.slug}.bookdbeyond.com/admin
                  </code>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`https://${newTenantInfo.subdomain || newTenantInfo.slug}.bookdbeyond.com/admin`, 'URL')}>
                    <Copy size={12} />
                  </Button>
                </div>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground text-xs">Email</span>
                  <code className="text-xs flex-1 text-right truncate">{newTenantInfo.admin_email}</code>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(newTenantInfo.admin_email, 'Email')}>
                    <Copy size={12} />
                  </Button>
                </div>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground text-xs">Password</span>
                  <code className="text-xs flex-1 text-right truncate">{newTenantInfo.admin_password}</code>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(newTenantInfo.admin_password, 'Password')}>
                    <Copy size={12} />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                💡 Until DNS for <code>{newTenantInfo.subdomain || newTenantInfo.slug}.bookdbeyond.com</code> is live, they can preview their salon on this server by sending the header{' '}
                <code className="bg-muted px-1 py-0.5 rounded text-[10px]">X-Tenant-Slug: {newTenantInfo.slug}</code>.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCredentialsOpen(false)} data-testid="close-credentials-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
